import { Component, Input, OnInit } from '@angular/core';
import { ControlContainer, FormControl, FormGroupDirective, FormsModule } from '@angular/forms';
import { BehaviorSubject, combineLatest, debounceTime, distinctUntilChanged, map, Observable } from 'rxjs';
import { CustomOperators } from '../../operators/custom-operators';
import { CommonModule } from '@angular/common';
import { ReactiveFormsModule } from '@angular/forms';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { MatOptionModule } from '@angular/material/core';
import { MatAutocompleteModule } from '@angular/material/autocomplete';

export interface Country {
  name: string;
  code: number;
  fontCode: string;
  flag: string;
}

@Component({
  selector: 'adm-country-code-selector',
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    MatFormFieldModule,
    MatInputModule,
    MatSelectModule,
    MatOptionModule,
    MatAutocompleteModule,
  ],
  templateUrl: './country-code-selector.component.html',
  styleUrl: './country-code-selector.component.scss',
  viewProviders: [
    {
      provide: ControlContainer,
      useExisting: FormGroupDirective,
    },
  ],
})
export class CountryCodeSelectorComponent implements OnInit {
  @Input()
  formItem!: FormControl<number>;
  defaultCountryCode: number = 45;
  private searchFilter = new BehaviorSubject<string>('');
  private searchFilter$ = this.searchFilter.asObservable().pipe(debounceTime(300), distinctUntilChanged());

  filterProperty = 'name';

  public displayFn = (): string => {
    const selectedCountry = this.countries.find(c => c.code === this.formItem.value);
    if (!selectedCountry) {
      return '';
    }

    return selectedCountry.flag + ' ' + selectedCountry.name + ' +' + selectedCountry.code;
  };

  //font codes and svgs are taken from -> https://flagicons.lipis.dev/
  countries: Array<Country> = [
    { name: 'Albanien', code: 355, fontCode: 'al', flag: '🇦🇱' },
    { name: 'Andorra', code: 376, fontCode: 'ad', flag: '🇦🇩' },
    { name: 'Belgien', code: 32, fontCode: 'be', flag: '🇧🇪' },
    { name: 'Bosnien-Hercegovina', code: 387, fontCode: 'ba', flag: '🇧🇦' },
    { name: 'Bulgarien', code: 359, fontCode: 'bg', flag: '🇧🇬' },
    { name: 'Danmark', code: 45, fontCode: 'dk', flag: '🇩🇰' },
    { name: 'Estland', code: 372, fontCode: 'ee', flag: '🇪🇪' },
    { name: 'Finland', code: 358, fontCode: 'fi', flag: '🇫🇮' },
    { name: 'Frankrig', code: 33, fontCode: 'fr', flag: '🇫🇷' },
    { name: 'Grækenland', code: 30, fontCode: 'gr', flag: '🇬🇷' },
    { name: 'Hviderusland', code: 375, fontCode: 'by', flag: '🇧🇾' },
    { name: 'Irland', code: 353, fontCode: 'ie', flag: '🇮🇪' },
    { name: 'Island', code: 354, fontCode: 'is', flag: '🇮🇸' },
    { name: 'Italien', code: 39, fontCode: 'it', flag: '🇮🇹' },
    { name: 'Kosovo', code: 383, fontCode: 'xk', flag: '🇽🇰' },
    { name: 'Kroatien', code: 385, fontCode: 'hr', flag: '🇭🇷' },
    { name: 'Letland', code: 371, fontCode: 'lv', flag: '🇱🇻' },
    { name: 'Liechtenstein', code: 423, fontCode: 'li', flag: '🇱🇮' },
    { name: 'Litauen', code: 370, fontCode: 'lt', flag: '🇱🇹' },
    { name: 'Luxembourg', code: 352, fontCode: 'lu', flag: '🇱🇺' },
    { name: 'Malta', code: 356, fontCode: 'mt', flag: '🇲🇹' },
    { name: 'Moldova', code: 373, fontCode: 'md', flag: '🇲🇩' },
    { name: 'Monaco', code: 377, fontCode: 'mc', flag: '🇲🇨' },
    { name: 'Montenegro', code: 382, fontCode: 'me', flag: '🇲🇪' },
    { name: 'Nederlandene', code: 31, fontCode: 'nl', flag: '🇳🇱' },
    { name: 'Nordmakedonien', code: 389, fontCode: 'mk', flag: '🇲🇰' },
    { name: 'Norge', code: 47, fontCode: 'no', flag: '🇳🇴' },
    { name: 'Polen', code: 48, fontCode: 'pl', flag: '🇵🇱' },
    { name: 'Portugal', code: 351, fontCode: 'pt', flag: '🇵🇹' },
    { name: 'Rumænien', code: 40, fontCode: 'ro', flag: '🇷🇴' },
    { name: 'San Marino', code: 378, fontCode: 'sm', flag: '🇸🇲' },
    { name: 'Schweiz', code: 41, fontCode: 'ch', flag: '🇨🇭' },
    { name: 'Serbien', code: 381, fontCode: 'rs', flag: '🇷🇸' },
    { name: 'Slovakiet', code: 421, fontCode: 'sk', flag: '🇸🇰' },
    { name: 'Slovenien', code: 386, fontCode: 'si', flag: '🇸🇮' },
    { name: 'Spanien', code: 34, fontCode: 'es', flag: '🇪🇸' },
    { name: 'Storbritannien', code: 44, fontCode: 'gb', flag: '🇬🇧' },
    { name: 'Sverige', code: 46, fontCode: 'se', flag: '🇸🇪' },
    { name: 'Tjekkiet', code: 420, fontCode: 'cz', flag: '🇨🇿' },
    { name: 'Tyskland', code: 49, fontCode: 'de', flag: '🇩🇪' },
    { name: 'Tyrkiet', code: 90, fontCode: 'tr', flag: '🇹🇷' },
    { name: 'Ukraine', code: 380, fontCode: 'ua', flag: '🇺🇦' },
    { name: 'Ungarn', code: 36, fontCode: 'hu', flag: '🇭🇺' },
    { name: 'Vatikanstaten', code: 379, fontCode: 'va', flag: '🇻🇦' },
    { name: 'Østrig', code: 43, fontCode: 'at', flag: '🇦🇹' },
  ];
  public filteredItems$!: Observable<Country[]>;

  ngOnInit(): void {
    if (this.formItem.value === null || this.formItem.value === undefined) {
      this.formItem.setValue(this.defaultCountryCode);
    }
    this.filteredItems$ = combineLatest([this.searchFilter$]).pipe(
      CustomOperators.filterNonNullAndNonEmpty(),
      map(([searchTerm]) =>
        this.countries.filter(item => item[this.filterProperty].toLocaleLowerCase().includes(searchTerm.toLocaleLowerCase()))
      )
    );
  }

  public onItemSelected(item: Country, event: any) {
    this.formItem.setValue(item.code);
    this.searchFilter.next('');
  }

  public onSearchChange(event: Event) {
    const target = event.target as HTMLInputElement;
    this.searchFilter.next(target.value);
  }
}
